import React, { useState } from 'react';
import { Shield, Lock, Eye, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSmoothScroll } from '../hooks/useSmoothScroll';

interface PrivacyFeatureProps {
  icon: React.ElementType;
  title: string;
  description: string;
  details: string[];
  isExpanded: boolean;
  onClick: () => void;
}

function PrivacyFeature({ icon: Icon, title, description, details, isExpanded, onClick }: PrivacyFeatureProps) {
  return (
    <div 
      onClick={onClick}
      className={`cursor-pointer group bg-white dark:bg-gray-800 rounded-xl p-6 transition-all duration-300
        ${isExpanded ? 'ring-2 ring-primary-500 shadow-lg' : 'hover:shadow-md'}`}
    >
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <div className={`absolute -inset-1 rounded-lg blur transition-opacity duration-300
            ${isExpanded ? 'opacity-30 bg-primary-500' : 'opacity-0 group-hover:opacity-20 bg-primary-500'}`} 
          />
          <Icon className="relative w-8 h-8 text-primary-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{description}</p>
          
          <div className={`space-y-2 transition-all duration-300 overflow-hidden
            ${isExpanded ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
            {details.map((detail, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Privacy() {
  const [expandedFeature, setExpandedFeature] = useState<number | null>(null);
  const navigate = useNavigate();
  const { scrollTo } = useSmoothScroll();

  const features = [
    {
      icon: Shield,
      title: "Data Encryption",
      description: "Your personal information is protected with industry-leading encryption.",
      details: [
        "End-to-end encryption for all sensitive data",
        "Regular security audits and penetration testing",
        "Compliance with global data protection standards"
      ]
    },
    {
      icon: Lock,
      title: "Secure Storage",
      description: "Advanced security measures protect your data at rest and in transit.",
      details: [
        "Distributed storage across secure data centers",
        "Regular backups with encryption",
        "Strict access controls and monitoring"
      ]
    },
    {
      icon: Eye,
      title: "Privacy Controls",
      description: "You have complete control over your privacy settings.",
      details: [
        "Granular privacy settings for your profile",
        "Option to remain anonymous in discussions",
        "Control over who can see your information"
      ]
    }
  ];

  const handleJoinWaitlist = () => {
    scrollTo('waitlist');
  };

  return (
    <section id="privacy" className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Your Privacy Matters</h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            We understand the importance of discretion and security. Here's how we protect your information.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {features.map((feature, index) => (
            <PrivacyFeature
              key={index}
              {...feature}
              isExpanded={expandedFeature === index}
              onClick={() => setExpandedFeature(expandedFeature === index ? null : index)}
            />
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={handleJoinWaitlist}
            className="group inline-flex items-center gap-2 bg-primary-500 text-white px-8 py-3 rounded-full hover:bg-primary-600 transition-all transform hover:scale-105"
          >
            Join Our Waitlist
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}